<script setup>
// SiteUp CRM (2026-09-19): Compartilhar virou um menu curto com três ações:
// copiar o link do cartão, enviar no chat interno e gerenciar acesso.
// Onde o host já tem um menu aberto (o cartão do quadro), este componente é
// montado em `variant="headless"` na raiz do cartão e o host chama as ações
// pela API exposta: assim o diálogo não morre quando o menu do host fecha.
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import KanbanShareDialog from './KanbanShareDialog.vue';
import KanbanShareSendDialog from './KanbanShareSendDialog.vue';
import {
  INTERNAL_CHAT_PERMISSIONS,
  buildCardShareUrl,
} from '../helpers/shareCard';

const props = defineProps({
  itemId: {
    type: [Number, String],
    required: true,
  },
  itemTitle: {
    type: String,
    default: '',
  },
  // Cartão completo: serve para saber quem já abre o cartão sem ida ao servidor.
  item: {
    type: Object,
    default: null,
  },
  canManage: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: 'button',
    validator: value => ['button', 'icon', 'headless'].includes(value),
  },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const { accountId } = useAccount();
const { checkPermissions } = usePolicy();

const triggerRef = ref(null);
const menuRef = ref(null);
const isOpen = ref(false);
const shareDialogRef = ref(null);
const sendDialogRef = ref(null);

const canUseInternalChat = computed(() =>
  checkPermissions(INTERNAL_CHAT_PERMISSIONS)
);

const { fixedPosition } = useDropdownPosition(triggerRef, menuRef, isOpen, {
  align: 'start',
});

const shareUrl = computed(() =>
  buildCardShareUrl({
    accountId: accountId.value,
    itemId: props.itemId,
    origin: window.location.origin,
  })
);

const focusTrigger = () => {
  const el = triggerRef.value?.$el ?? triggerRef.value;
  el?.focus?.();
};

const closeMenu = ({ restoreFocus = true } = {}) => {
  if (!isOpen.value) return;
  isOpen.value = false;
  if (restoreFocus) focusTrigger();
};

const openMenu = async () => {
  isOpen.value = true;
  await nextTick();
  menuRef.value?.querySelector('button')?.focus();
};

const toggleMenu = () => (isOpen.value ? closeMenu() : openMenu());

onClickOutside(menuRef, () => closeMenu({ restoreFocus: false }), {
  ignore: [triggerRef],
});
onKeyStroke('Escape', event => {
  if (!isOpen.value) return;
  event.preventDefault();
  closeMenu();
});

const moveFocus = (event, step) => {
  const items = [...(menuRef.value?.querySelectorAll('button') || [])];
  if (!items.length) return;
  event.preventDefault();
  const current = items.indexOf(document.activeElement);
  const next = (current + step + items.length) % items.length;
  items[next].focus();
};

const copyLink = async () => {
  closeMenu({ restoreFocus: false });
  if (!shareUrl.value) {
    useAlert(t('KANBAN_SHARING.LINK.FAILED'));
    return;
  }
  try {
    await copyTextToClipboard(shareUrl.value);
    useAlert(t('KANBAN_SHARING.LINK.COPIED'));
  } catch {
    useAlert(t('KANBAN_SHARING.LINK.FAILED'));
  }
};

const openSend = () => {
  closeMenu({ restoreFocus: false });
  sendDialogRef.value?.open();
};

const openManage = () => {
  closeMenu({ restoreFocus: false });
  shareDialogRef.value?.open();
};

defineExpose({ copyLink, openSend, openManage, canUseInternalChat });
</script>

<template>
  <span class="contents">
    <template v-if="variant !== 'headless'">
      <Button
        v-if="variant === 'icon'"
        ref="triggerRef"
        type="button"
        data-testid="kanban-share-button"
        color="slate"
        variant="ghost"
        size="sm"
        icon="i-ph-share-network"
        :aria-label="t('KANBAN_SHARING.ACTIONS.SHARE')"
        :aria-expanded="isOpen"
        aria-haspopup="menu"
        @click.stop="toggleMenu"
      />
      <Button
        v-else
        ref="triggerRef"
        type="button"
        data-testid="kanban-share-button"
        color="slate"
        variant="outline"
        size="sm"
        icon="i-ph-share-network"
        :label="t('KANBAN_SHARING.ACTIONS.SHARE')"
        :aria-expanded="isOpen"
        aria-haspopup="menu"
        @click.stop="toggleMenu"
      />
    </template>

    <Teleport v-if="isOpen" to="body">
      <div
        ref="menuRef"
        role="menu"
        :aria-label="t('KANBAN_SHARING.MENU.LABEL')"
        data-testid="kanban-share-menu"
        :class="fixedPosition.class"
        :style="fixedPosition.style"
        class="flex w-56 flex-col overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 py-1 shadow-lg"
        @click.stop
        @keydown.down="moveFocus($event, 1)"
        @keydown.up="moveFocus($event, -1)"
      >
        <button
          type="button"
          role="menuitem"
          data-testid="kanban-share-menu-copy"
          class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          @click="copyLink"
        >
          <span class="i-ph-link size-4 shrink-0" />
          {{ t('KANBAN_SHARING.MENU.COPY_LINK') }}
        </button>
        <button
          v-if="canUseInternalChat"
          type="button"
          role="menuitem"
          data-testid="kanban-share-menu-send"
          class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          @click="openSend"
        >
          <span class="i-ph-paper-plane-tilt size-4 shrink-0" />
          {{ t('KANBAN_SHARING.MENU.SEND_INTERNAL_CHAT') }}
        </button>
        <button
          v-if="canManage"
          type="button"
          role="menuitem"
          data-testid="kanban-share-menu-manage"
          class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
          @click="openManage"
        >
          <span class="i-ph-lock-key size-4 shrink-0" />
          {{ t('KANBAN_SHARING.MENU.MANAGE_ACCESS') }}
        </button>
      </div>
    </Teleport>

    <KanbanShareSendDialog
      v-if="canUseInternalChat"
      ref="sendDialogRef"
      :item-id="itemId"
      :item-title="itemTitle"
      :item="item"
      :can-manage="canManage"
      @access-granted="emit('updated', $event)"
    />
    <KanbanShareDialog
      v-if="canManage"
      ref="shareDialogRef"
      :item-id="itemId"
      :item-title="itemTitle"
      @updated="emit('updated', $event)"
    />
  </span>
</template>
